import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Leaf,
  Coffee,
  UtensilsCrossed,
  Sparkles,
  ShieldCheck,
  Recycle,
  Heart,
  Globe,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

// Hero & supporting imagery (user-provided assets)
const HERO_IMG =
  '/assets/images/product_5.png';
const CARD_CUPS_IMG =
  '/assets/images/product_4.png';
const CARD_CUTLERY_IMG =
  '/assets/images/1.jpg';
const SUSTAIN_IMG =
  '/assets/images/3.jpg';
const INTRO_IMG =
  '/assets/images/tastymaxi_7.jpg';

const ProductRange = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  useEffect(() => {
    document.title =
      language === 'nl'
        ? 'Productassortiment | Magomeal'
        : 'Product Range | Magomeal';
  }, [language]);

  const isNL = language === 'nl';

  const categories = [
    {
      to: '/edible-cups',
      title: isNL ? 'Eetbare Bekers' : 'Edible Cups',
      tagline: isNL ? 'Nice to eat you!' : 'Nice to eat you!',
      description: isNL
        ? 'Knapperige, heerlijke bekers die je kunt eten of composteren, perfect voor koffie, thee, desserts en signature drinks.'
        : 'Crunchy, delicious cups you can eat or compost, perfect for coffee, tea, desserts and signature drinks.',
      image: CARD_CUPS_IMG,
      icon: <Coffee size={28} />,
      cta: isNL ? 'Ontdek Eetbare Bekers' : 'Discover Edible Cups',
      accent: 'cups',
    },
    {
      to: '/cutlery-tableware',
      title: isNL ? 'Bestek & Servies' : 'Cutlery & Tableware',
      tagline: isNL ? 'Eet je bestek op' : 'Eat your cutlery',
      description: isNL
        ? 'Eetbare lepels, vorken, roerstaafjes en meer: stevig, smaakvol en 100% biologisch afbreekbaar.'
        : 'Edible spoons, forks, stirrers and more: sturdy, tasty and 100% biodegradable.',
      image: CARD_CUTLERY_IMG,
      icon: <UtensilsCrossed size={28} />,
      cta: isNL ? 'Ontdek Bestek & Servies' : 'Discover Cutlery & Tableware',
      accent: 'cutlery',
    },
  ];

  const benefits = [
    {
      icon: <Leaf size={26} />,
      title: isNL ? '100% Plantaardig' : '100% Plant-Based',
      desc: isNL
        ? 'Gemaakt van natuurlijke ingrediënten: geen plastic, geen coatings, geen compromissen.'
        : 'Made from natural ingredients: no plastic, no coatings, no compromises.',
    },
    {
      icon: <Recycle size={26} />,
      title: isNL ? 'Zero-Waste' : 'Zero-Waste',
      desc: isNL
        ? 'Eet je verpakking op of composteer hem in 6 weken. Niets gaat verloren.'
        : 'Eat your packaging or compost it within 6 weeks. Nothing goes to waste.',
    },
    {
      icon: <ShieldCheck size={26} />,
      title: isNL ? 'Gecertificeerd Veilig' : 'Certified Safe',
      desc: isNL
        ? 'FSSC 22000, Vegan, Halal en FDA-goedgekeurd voor jouw gemoedsrust.'
        : 'FSSC 22000, Vegan, Halal and FDA-approved for your peace of mind.',
    },
    {
      icon: <Sparkles size={26} />,
      title: isNL ? 'Heerlijk & Knapperig' : 'Delicious & Crunchy',
      desc: isNL
        ? 'Lichtzoet, knapperig en gemaakt om elke hap of slok te verbeteren.'
        : 'Lightly sweet, crunchy and made to enhance every bite or sip.',
    },
    {
      icon: <Heart size={26} />,
      title: isNL ? 'Premium Ervaring' : 'Premium Experience',
      desc: isNL
        ? 'Til jouw merk en gastenervaring naar een hoger niveau met onvergetelijke serveerstukken.'
        : 'Elevate your brand and guest experience with unforgettable serveware.',
    },
    {
      icon: <Globe size={26} />,
      title: isNL ? '75% Minder CO₂' : '75% Less CO₂',
      desc: isNL
        ? 'Onze productie stoot tot 75% minder CO₂ uit dan traditionele plastic alternatieven.'
        : 'Our production emits up to 75% less CO₂ than traditional plastic alternatives.',
    },
  ];

  return (
    <div className="product-range-page">
      {/* ============ HERO ============ */}
      <section className="pr-hero">
        <div className="pr-hero-image-wrap">
          <img
            src={HERO_IMG}
            alt={isNL ? 'Duurzaam productassortiment Magomeal' : 'Magomeal sustainable product range'}
            className="pr-hero-image"
            loading="eager"
          />
          <div className="pr-hero-overlay" aria-hidden="true" />
        </div>
        <div className="container pr-hero-content">
          <span className="pr-eyebrow">
            {isNL ? 'Productassortiment' : 'Product Range'}
          </span>
          <h1 className="pr-hero-title">
            {isNL
              ? 'De toekomst van duurzaam serveren, om op te eten.'
              : 'The future of sustainable serveware, made to be eaten.'}
          </h1>
          <p className="pr-hero-subtitle">
            {isNL
              ? 'Ontdek onze volledige collectie eetbare bekers, bestek en servies, ontworpen om plastic te vervangen door iets heerlijks.'
              : 'Discover our full collection of edible cups, cutlery and tableware, designed to replace plastic with something delicious.'}
          </p>
          <div className="pr-hero-actions">
            <a href="#categories" className="btn-primary">
              {isNL ? 'Bekijk Categorieën' : 'Explore Categories'}
              <ArrowRight size={18} />
            </a>
            <Link to="/contact" className="btn-secondary pr-btn-secondary">
              {isNL ? 'Neem Contact Op' : 'Talk to Sales'}
            </Link>
          </div>
        </div>
      </section>

      {/* ============ INTRO ============ */}
      <section className="pr-intro">
        <div className="container pr-intro-grid">
          <div className="pr-intro-text">
            <span className="pr-section-eyebrow">
              {isNL ? 'Over onze producten' : 'About our products'}
            </span>
            <h2 className="pr-section-title">
              {isNL
                ? 'Eén missie. Twee productlijnen. Onbeperkte mogelijkheden.'
                : 'One mission. Two product lines. Endless possibilities.'}
            </h2>
            <p className="pr-section-body">
              {isNL
                ? 'Bij Magomeal geloven we dat duurzaamheid lekker moet smaken. Daarom hebben we eetbare bekers en bestek ontwikkeld die net zo functioneel zijn als hun plastic tegenhangers, maar zonder de afval. Of je nu een evenement organiseert, een café runt of gewoon thuis geniet: ons assortiment biedt voor iedereen een oplossing.'
                : 'At Magomeal we believe sustainability should taste delicious. That is why we have developed edible cups and cutlery that are just as functional as their plastic counterparts, without the waste. Whether you are running an event, a café, or simply enjoying at home: our range has a solution for every moment.'}
            </p>
            <ul className="pr-intro-list">
              <li>
                <Leaf size={18} /> {isNL ? 'Volledig biologisch afbreekbaar' : 'Fully biodegradable'}
              </li>
              <li>
                <Sparkles size={18} /> {isNL ? 'Heerlijk en knapperig' : 'Delicious and crunchy'}
              </li>
              <li>
                <ShieldCheck size={18} /> {isNL ? 'Gecertificeerd veilig' : 'Certified food-safe'}
              </li>
            </ul>
          </div>
          <div className="pr-intro-image-wrap">
            <img src={INTRO_IMG} alt={isNL ? 'Magomeal eetbaar bestek' : 'Magomeal edible cutlery'} loading="lazy" />
          </div>
        </div>
      </section>

      {/* ============ CATEGORIES ============ */}
      <section id="categories" className="pr-categories">
        <div className="container">
          <div className="pr-section-header">
            <span className="pr-section-eyebrow">
              {isNL ? 'Categorieën' : 'Categories'}
            </span>
            <h2 className="pr-section-title">
              {isNL ? 'Kies je productlijn' : 'Choose your product line'}
            </h2>
            <p className="pr-section-body">
              {isNL
                ? 'Verken onze twee belangrijkste categorieën en vind het juiste eetbare alternatief voor jouw behoeften.'
                : 'Browse our two core categories and find the perfect edible alternative for your needs.'}
            </p>
          </div>

          <div className="pr-category-grid">
            {categories.map((cat) => (
              <Link
                to={cat.to}
                key={cat.to}
                className={`pr-category-card pr-${cat.accent}`}
                aria-label={cat.title}
              >
                <div className="pr-category-image-wrap">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="pr-category-image"
                    loading="lazy"
                  />
                  <div className="pr-category-icon">{cat.icon}</div>
                </div>
                <div className="pr-category-body">
                  <span className="pr-category-tagline">{cat.tagline}</span>
                  <h3 className="pr-category-title">{cat.title}</h3>
                  <p className="pr-category-desc">{cat.description}</p>
                  <span className="pr-category-link">
                    {cat.cta} <ArrowRight size={18} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BENEFITS ============ */}
      <section className="pr-benefits">
        <div className="container">
          <div className="pr-section-header">
            <span className="pr-section-eyebrow">
              {isNL ? 'Waarom Magomeal' : 'Why Magomeal'}
            </span>
            <h2 className="pr-section-title">
              {isNL
                ? 'Voordelen die je proeft, en die de planeet voelt.'
                : 'Benefits you can taste, and the planet can feel.'}
            </h2>
          </div>
          <div className="pr-benefits-grid">
            {benefits.map((b, i) => (
              <div className="pr-benefit-card" key={i}>
                <div className="pr-benefit-icon">{b.icon}</div>
                <h3 className="pr-benefit-title">{b.title}</h3>
                <p className="pr-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SUSTAINABILITY ============ */}
      <section className="pr-sustain">
        <div className="container pr-sustain-grid">
          <div className="pr-sustain-image-wrap">
            <img
              src={SUSTAIN_IMG}
              alt={isNL ? 'Duurzaamheid bij Magomeal' : 'Sustainability at Magomeal'}
              loading="lazy"
            />
          </div>
          <div className="pr-sustain-text">
            <span className="pr-section-eyebrow light">
              {isNL ? 'Duurzaamheid' : 'Sustainability'}
            </span>
            <h2 className="pr-section-title light">
              {isNL
                ? 'Klein product. Grote impact.'
                : 'Small product. Big impact.'}
            </h2>
            <p className="pr-section-body light">
              {isNL
                ? 'Elke eetbare beker en elk eetbaar bestuk vervangt een stuk plastic dat anders in de oceaan, op een stortplaats of als microplastic in onze voedselketen zou eindigen. Onze producten zijn ontworpen om het probleem op te lossen, zonder concessies aan smaak of functionaliteit.'
                : 'Every edible cup and piece of cutlery replaces a piece of plastic that would otherwise end up in the ocean, a landfill, or as microplastic in our food chain. Our products are designed to solve the problem, without compromising on taste or function.'}
            </p>
            <ul className="pr-sustain-stats">
              <li>
                <span className="pr-stat-number">75%</span>
                <span className="pr-stat-label">
                  {isNL ? 'Minder CO₂-uitstoot' : 'Less CO₂ emissions'}
                </span>
              </li>
              <li>
                <span className="pr-stat-number">6 wk</span>
                <span className="pr-stat-label">
                  {isNL ? 'Volledig composteerbaar' : 'Fully compostable'}
                </span>
              </li>
              <li>
                <span className="pr-stat-number">0%</span>
                <span className="pr-stat-label">
                  {isNL ? 'Plastic of coating' : 'Plastic or coating'}
                </span>
              </li>
            </ul>
            <Link to="/certifications" className="btn-secondary pr-btn-light">
              {isNL ? 'Bekijk Certificeringen' : 'View Certifications'}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="pr-cta">
        <div className="container">
          <div className="pr-cta-card">
            <h2 className="pr-cta-title">
              {isNL
                ? 'Klaar om over te stappen op eetbaar?'
                : 'Ready to switch to edible?'}
            </h2>
            <p className="pr-cta-body">
              {isNL
                ? 'Of je nu groothandelsprijzen wilt, een samplebox wilt aanvragen of een maatwerkoplossing zoekt, we helpen je graag verder.'
                : 'Whether you need wholesale pricing, a sample box or a tailored solution, our team is ready to help.'}
            </p>
            <div className="pr-cta-actions">
              <Link to="/shop" className="btn-primary">
                {isNL ? 'Naar de Shop' : 'Visit the Shop'}
                <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn-secondary">
                {isNL ? 'Contact Sales' : 'Contact Sales'}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductRange;
